import React from 'react'
import { useDispatch } from 'react-redux'
import { Switch, Route, useHistory, useRouteMatch } from 'react-router-dom'

import { submitStep1, submitStep2, submitStep2b, resetMcfState, IMedicareComplaintReducer, resetLoadingState } from 'redux/reducers/medicare-complaint-form'
import { ClickEventOptions, sendTealiumClickEvent } from 'utils/sendTealiumEvent'

import { Step0, Step1, Step2, Step2b, Success } from './steps'

import * as english from 'i18n/en'

export interface MedicareComplaintFormProps {
  showTimeoutMessage: boolean
  setShowTimeoutMessage: (showTimeoutMessage: boolean) => void
}

export const MedicareComplaintForm: React.FC<MedicareComplaintFormProps> = (
  { showTimeoutMessage, setShowTimeoutMessage }
) => {
  const history = useHistory()
  const match = useRouteMatch()
  const matchedPath = match?.path || ''
  const routes = {
    step0: `${matchedPath}`,
    step1: `${matchedPath}/step1`,
    step2: `${matchedPath}/step2`,
    step2b: `${matchedPath}/step2b`,
    success: `${matchedPath}/success`
  }

  const dispatch = useDispatch()

  return (
    <div className='my-c-complaint-form'>
      <Switch>
        <Route exact path={matchedPath}>
          <Step0
            showTimeoutMessage={showTimeoutMessage}
            setShowTimeoutMessage={setShowTimeoutMessage}
            handleOnSubmit={() => {
              sendTealiumClickEvent({
                event_action: 'Begin Form - Button',
                event_label: 'CTA: ' + english.mcfStep0Page['File a Complaint'],
                event_name: 'medicare_complaint_form_started',
                text: english.mcfStep0Page['File a Complaint'],
                event_type: 'ui interaction',
                page: 'Landing Page'
              } as ClickEventOptions)
              history.push(routes.step1)
            }}
          />
        </Route>
        <Route exact path={routes.step1}>
          <Step1
            handleOnBack={() => {
              sendTealiumClickEvent(
                {
                  event_action: english.mcfStep1Page['Your Medicare information'],
                  event_label: 'CTA: ' + english.mcfStep1Page.Cancel,
                  event_name: 'medicare_complaint_form_ui_clicked',
                  text: english.mcfStep1Page.Cancel,
                  event_type: 'ui interaction',
                  target_location: routes.step0,
                  step_number: '1',
                  page: 'Verification Page'
                } as ClickEventOptions
              )
              history.push(routes.step0)
              dispatch(resetLoadingState())
              // Reset form state because going back from step 1 is 'cancelling' the complaint
              dispatch(resetMcfState())
            }}
            handleOnAltForm={() => {
              sendTealiumClickEvent(
                {
                  event_action: english.mcfStep1Page['Your Medicare information'],
                  event_label: 'CTA: ' + english.mcfStep1Page.alert,
                  event_name: 'medicare_complaint_form_ui_clicked',
                  text: english.mcfStep1Page.alert,
                  header: english.mcfStep1Page['Your Medicare information'],
                  event_type: 'ui interaction',
                  target_location: routes.step2b,
                  step_number: '1',
                  page: 'Verification Page'
                } as ClickEventOptions
              )
              history.push(routes.step2b)
              dispatch(resetLoadingState())
            }}
            handleOnError={() => {
              // form error
              window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
            }}
            handleOnSubmit={async (data: IMedicareComplaintReducer) => {
              const thunkResult = await dispatch(submitStep1(data)) as any
              if (!thunkResult.error) {
                sendTealiumClickEvent(
                  {
                    event_action: english.mcfStep2Page['Your contact information'],
                    event_label: 'CTA: ' + english.mcfStep1Page.Next,
                    event_name: 'medicare_complaint_form_ui_clicked',
                    text: english.mcfStep1Page.Next,
                    header: english.mcfStep1Page['Your Medicare information'],
                    event_type: 'ui interaction',
                    step_number: '1',
                    page: 'Verification Page'
                  } as ClickEventOptions
                )
                history.push(routes.step2)
                dispatch(resetLoadingState())
              } else {
                // network error
                window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
                sendTealiumClickEvent({
                  event_action: english.mcfStep1Page.step1Error.text,
                  event_label: 'CTA: ' + english.mcfStep1Page.Next,
                  event_name: 'medicare_complaint_form_error_occurred',
                  text: english.mcfStep1Page.Next,
                  header: english.mcfStep1Page['Your Medicare information'],
                  event_type: 'ui interaction',
                  error_message: english.mcfStep1Page.step1Error.heading,
                  error_code: '404',
                  error_type: english.mcfStep1Page.Next + ' error',
                  step_number: '1',
                  page: 'Verification Page'
                } as ClickEventOptions)
              }
            }}
          />
        </Route>
        <Route exact path={routes.step2}>
          <Step2
            handleOnBack={() => {
              sendTealiumClickEvent(
                {
                  event_action: english.mcfStep2Page['Your contact information'],
                  event_label: 'CTA: ' + english.mcfStep2Page.Back,
                  event_name: 'medicare_complaint_form_ui_clicked',
                  text: english.mcfStep2Page.Back,
                  header: english.mcfStep2Page['Your contact information'],
                  event_type: 'ui interaction',
                  step_number: '2',
                  page: 'Complaint Page'
                } as ClickEventOptions
              )
              history.push(routes.step1)
              dispatch(resetLoadingState())
            }}
            handleOnError={() => {
              // form error
              window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
            }}
            handleOnSubmit={async (data) => {
              const thunkResult = await dispatch(submitStep2(data)) as any
              if (!thunkResult.error) {
                sendTealiumClickEvent(
                  {
                    event_action: 'Submit Step 2',
                    event_label: `Preferred Language: ${data.preferredLanguage} , Preferred Callback Time : ${data.preferredCallbackTime} , Benefits, drugs, or services: ${data.issueConcerningPlan}`,
                    event_name: 'medicare_complaint_form_ui_clicked',
                    text: english.mcfStep2Page.Submit,
                    header: english.mcfStep2Page['Your contact information'],
                    event_type: 'conversion',
                    step_number: '2',
                    preferred_language: data.preferredLanguage,
                    access_complaint: data.issueConcerningPlan,
                    prefer_call_back_time: data.preferredCallbackTime,
                    page: 'Complaint Page'
                  } as ClickEventOptions
                )
                history.push(routes.success)
                dispatch(resetLoadingState())
              } else {
                // network error
                window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
                sendTealiumClickEvent({
                  event_action: english.mcfStep2Page.step2Error.text,
                  event_label: 'CTA: ' + english.mcfStep2Page.Submit,
                  event_name: 'medicare_complaint_form_submit_failed',
                  text: english.mcfStep2Page.Submit,
                  event_type: 'ui interaction',
                  error_message: english.mcfStep2Page.step2Error.text,
                  error_code: '404',
                  error_type: english.mcfStep2Page.Submit + ' error',
                  step_number: '2',
                  page: 'Complaint Page'
                }as ClickEventOptions)
              }
            }}
            handleOnPreventUserStep={() => {
              history.push(routes.step0)
            }}
          />
        </Route>
        <Route exact path={routes.step2b}>
          <Step2b
            handleOnBack={() => {
              sendTealiumClickEvent(
                {
                  event_action: english.mcfStep2Page['Your contact information'],
                  event_label: 'CTA: ' + english.mcfStep2Page.Back,
                  event_name: 'medicare_complaint_form_ui_clicked',
                  text: english.mcfStep2Page.Back,
                  header: english.mcfStep2Page['Your contact information'],
                  event_type: 'ui interaction',
                  step_number: '2b',
                  page: 'Complaint Page'
                } as ClickEventOptions
              )
              history.push(routes.step1)
              dispatch(resetLoadingState())
            }}
            handleOnError={() => {
              // form error
              window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
            }}
            handleOnSubmit={async (data) => {
              const thunkResult = await dispatch(submitStep2b(data)) as any
              if (!thunkResult.error) {
                sendTealiumClickEvent(
                  {
                    event_action: 'Submit Step 2b',
                    event_label: `Preferred Language: ${data.preferredLanguage} , Preferred Callback Time : ${data.preferredCallbackTime} , Benefits, drugs, or services: ${data.issueConcerningPlan}`,
                    event_name: 'medicare_complaint_form_ui_clicked',
                    text: english.mcfStep2Page.Submit,
                    event_type: 'conversion',
                    header: english.mcfStep2Page['Your contact information'],
                    step_number: '2b',
                    page: 'Complaint Page',
                    preferred_language: data.preferredLanguage,
                    access_complaint: data.issueConcerningPlan,
                    prefer_call_back_time: data.preferredCallbackTime
                  } as ClickEventOptions
                )
                history.push(routes.success)
                dispatch(resetLoadingState())
              } else {
                // network error
                window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
                sendTealiumClickEvent({
                  event_action: english.mcfStep2Page.step2Error.text,
                  event_label: 'CTA: ' + english.mcfStep2Page.Submit,
                  event_name: 'medicare_complaint_form_submit_failed',
                  text: english.mcfStep2Page.Submit,
                  event_type: 'ui interaction',
                  error_message: english.mcfStep2Page.step2Error.text,
                  error_code: '404',
                  error_type: english.mcfStep2Page.Submit + ' error',
                  step_number: '2b',
                  page: 'Complaint Page'
                } as ClickEventOptions)
              }
            }}
          />
        </Route>
        <Route
          exact path={routes.success}
        >
          <Success
            handleOnInitialLoad={() => {
              dispatch(resetMcfState())
              sendTealiumClickEvent({
                event_action: 'Step 3 Complete',
                event_label: 'Complaint Submitted',
                event_name: 'medicare_complaint_form_submitted',
                event_type: 'ui interaction',
                page: 'Confirmation Page'
              } as ClickEventOptions)
            }}
          />
        </Route>
        <Route>
          <Step0
            showTimeoutMessage={showTimeoutMessage}
            setShowTimeoutMessage={setShowTimeoutMessage}
            handleOnSubmit={() => { history.push(routes.step1) }}
          />
        </Route>
      </Switch>
    </div>
  )
}
